import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { INCIDENT_TRIAL_MISSION, isOptimizationMission, optimizationCall, OPTIMIZATION_CHANGED, SNAPSHOT_MISSION, type OptimizationAction, type OptimizationStatus, type OptimizationTechnique } from '../../lib/optimization';
import { optimizationCopy } from '../../lib/strings/optimization';
import { intlTag } from '../../lib/format';
import { useLocale } from '../../state/useLocale';
import { Button, LinkButton } from '../../ui';
import { MeasuredAgentSetup } from '../agent/MeasuredAgentSetup';
import { measuredClientFromUsage, measuredModelFromUsage, replayMeasuredModel } from '../../lib/measured-client';
import { optimizationPracticeCopy, optimizationPracticeFor } from '../../lib/strings/optimization-practice';
import { snapshotCoachingCopy } from '../../lib/strings/snapshot-coaching';
import { incidentTrialCopy } from '../../lib/strings/incident-trial';

const CHOICES: OptimizationTechnique[] = ['scope-context', 'restart', 'right-size-model'];

/**
 * One owner-led experiment, independent of exercise completion and XP.
 * Missing measurement is a visible, recoverable state, never a zero bill.
 * The server owns the experiment and choice, so reload cannot lose either.
 */
export function OptimizationJourney({ missionId, userId, passed, onReplay, open = true, onComplete, onContinue, onTransfer, target = 'tab', blocked = false, pollMs = 2500 }: {
  missionId: string;
  userId: string | null;
  passed: boolean;
  onReplay(): void | Promise<void>;
  open?: boolean;
  onComplete?(complete: boolean): void;
  onContinue?(): void;
  onTransfer?(): void;
  target?: 'tab' | 'server';
  /** The workspace is busy or its allowance is spent. Reading and explicit skip remain available. */
  blocked?: boolean;
  pollMs?: number;
}) {
  const { locale } = useLocale();
  const s = optimizationCopy[locale];
  const practice = optimizationPracticeCopy[locale];
  const snapshotCopy = snapshotCoachingCopy[locale];
  const incidentCopy = incidentTrialCopy[locale];
  const incident = missionId === INCIDENT_TRIAL_MISSION;
  const eligible = isOptimizationMission(missionId) && (!incident || target === 'server');
  const lesson = optimizationPracticeFor(missionId, locale);
  const identity = useId();
  const [storedStatus, setStoredStatus] = useState<{ userId: string | null; missionId: string; status: OptimizationStatus } | null>(null);
  // Account and route changes must not paint the previous owner's figures,
  // even in the render before effects clean up an in-flight request.
  const status = storedStatus?.userId === userId && storedStatus?.missionId === missionId ? storedStatus.status : null;
  const setStatus = useCallback((next: OptimizationStatus | null) => {
    setStoredStatus(next ? { userId, missionId, status: next } : null);
  }, [userId, missionId]);
  const [problem, setProblem] = useState<'sign-in' | 'unavailable' | 'refused' | 'reset' | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmStart, setConfirmStart] = useState(false);
  const [manual, setManual] = useState(false);
  const [choice, setChoice] = useState<OptimizationTechnique | null>(null);
  const [refresh, setRefresh] = useState(0);
  const version = useRef(0);
  const subject = useRef({ userId, missionId });
  subject.current = { userId, missionId };
  const workspaceBlocked = useRef(blocked);
  workspaceBlocked.current = blocked;
  const session = status?.session ?? null;
  const feedback = status?.feedback ?? null;
  const comparison = feedback?.practice;
  const complete = session?.phase === 'complete' && status?.measurement.state === 'ready' && comparison?.after !== null && comparison?.after !== undefined;
  const usd = (micro: number) => new Intl.NumberFormat(intlTag(locale), { style: 'currency', currency: 'USD', minimumFractionDigits: 4, maximumFractionDigits: 6 }).format(micro / 1e6);
  const number = (n: number) => new Intl.NumberFormat(intlTag(locale)).format(n);
  const delta = comparison?.after ? Math.abs(comparison.before.costMicro - comparison.after.costMicro) : 0;
  const deltaShare = comparison?.before.costMicro ? delta / comparison.before.costMicro : 0;
  const relativeCost = deltaShare >= 0.0005
    ? new Intl.NumberFormat(intlTag(locale), { style: 'percent', maximumFractionDigits: 1 }).format(deltaShare)
    : null;

  useEffect(() => { onComplete?.(complete); }, [complete, onComplete]);
  useEffect(() => { if (complete) window.dispatchEvent(new Event(OPTIMIZATION_CHANGED)); }, [complete, missionId, userId]);
  useEffect(() => {
    setChoice(session?.technique ?? null);
  }, [session?.id, session?.technique]);

  useEffect(() => {
    version.current++;
    setStatus(null); setProblem(null); setChoice(null); setConfirmStart(false); setManual(false); setBusy(false);
  }, [missionId, userId, setStatus]);

  useEffect(() => {
    if (!open || !userId || !eligible) return;
    let alive = true;
    let timer = 0;
    let pending = false;
    const read = async () => {
      if (pending || document.visibilityState === 'hidden') return;
      pending = true;
      const at = version.current;
      const answer = await optimizationCall(missionId);
      pending = false;
      if (!alive || at !== version.current) return;
      setLoading(false);
      if (answer.ok) {
        setStatus(answer.status); setProblem((was) => was === 'reset' ? was : null);
        timer = window.setTimeout(() => void read(), pollMs);
      } else {
        setProblem(answer.error);
        // An unavailable endpoint must become a Retry state, not an endless spinner.
      }
    };
    const wake = () => { window.clearTimeout(timer); void read(); };
    setLoading(true);
    void read();
    window.addEventListener(OPTIMIZATION_CHANGED, wake);
    document.addEventListener('visibilitychange', wake);
    return () => { alive = false; window.clearTimeout(timer); window.removeEventListener(OPTIMIZATION_CHANGED, wake); document.removeEventListener('visibilitychange', wake); };
  }, [missionId, userId, open, eligible, passed, refresh, pollMs, setStatus]);

  const reset = useCallback(async () => {
    if (workspaceBlocked.current) return;
    const owner = subject.current;
    setBusy(true); setProblem(null);
    try { await onReplay(); }
    catch { if (subject.current.userId === owner.userId && subject.current.missionId === owner.missionId) setProblem('reset'); }
    finally { if (subject.current.userId === owner.userId && subject.current.missionId === owner.missionId) setBusy(false); }
  }, [onReplay]);

  const act = async (action: OptimizationAction) => {
    if (busy || workspaceBlocked.current) return;
    setBusy(true); setProblem(null);
    version.current++;
    const owner = userId;
    const ref = missionId;
    const answer = await optimizationCall(missionId, action);
    if (subject.current.userId !== owner || subject.current.missionId !== ref) return;
    if (!answer.ok) { setProblem(answer.error); setBusy(false); return; }
    setStatus(answer.status); setConfirmStart(false); setManual(false);
    window.dispatchEvent(new Event(OPTIMIZATION_CHANGED));
    setRefresh((n) => n + 1);
    if (workspaceBlocked.current) { setProblem('reset'); setBusy(false); return; }
    try { await onReplay(); }
    catch { if (subject.current.userId === owner && subject.current.missionId === ref) setProblem('reset'); }
    finally { if (subject.current.userId === owner && subject.current.missionId === ref) setBusy(false); }
  };

  if (!open || !eligible) return null;
  const snapshot = missionId === SNAPSHOT_MISSION;
  const coaching = status?.coaching;
  const repeatedGit = snapshot && coaching?.kind === 'repeat-git-inspection' ? coaching : null;
  const rawIncident = incident && coaching?.kind === 'aggregate-in-workspace' ? coaching : null;
  const choices: OptimizationTechnique[] = incident ? (rawIncident ? ['scope-context'] : [])
    : snapshot && !repeatedGit ? CHOICES.filter((technique) => technique !== 'scope-context') : CHOICES;
  const labels: Record<OptimizationTechnique, string> = { 'right-size-model': s.model, 'scope-context': incident ? incidentCopy.lesson : repeatedGit ? snapshotCopy.title : s.context, restart: s.restart };
  const baselineClient = measuredClientFromUsage(feedback?.usage);
  const baselineModel = measuredModelFromUsage(feedback?.usage, status?.baselineModels);
  const smallerModel = replayMeasuredModel(baselineModel, true);
  const replayModel = replayMeasuredModel(baselineModel, session?.technique === 'right-size-model');
  const gitCommand = repeatedGit ? `git ${repeatedGit.command}` : '';
  const instructions: Record<OptimizationTechnique, string> = { 'right-size-model': smallerModel ? baselineClient === 'codex' ? s.modelHowCodex : s.modelHow : practice.smallerUnavailable, 'scope-context': incident ? incidentCopy.instruction : repeatedGit ? snapshotCopy.instruction(gitCommand) : lesson.instruction, restart: practice.restart };
  const canChoose = choice !== null && choices.includes(choice);
  const phase = session?.phase;
  const measurement = status?.measurement;
  const measurable = measurement?.state === 'ready' && feedback?.attempt.by === 'agent' && (comparison?.before.costMicro ?? feedback?.attempt.costMicro ?? 0) > 0;
  const keepBaseline = snapshot && phase === 'review' && measurable && !repeatedGit;
  const keepIncident = incident && phase === 'review' && measurable && !rawIncident;
  const missing = measurement?.state === 'unmeasured' || (!session && passed);
  const step = phase === 'complete' ? 3 : phase === 'replay' ? 2 : phase === 'review' ? 1 : 0;
  const continueWithout = () => { setManual(true); onContinue?.(); };

  return <section className="optimization-journey block" aria-labelledby={`${identity}-title`}>
    <h2 id={`${identity}-title`}>{passed ? s.passedTitle : s.title}</h2>
    <p>{s.intro}</p>
    <ol className="optimization-journey__steps" aria-label={s.title}>
      {[s.baseline, s.review, s.replay, s.result].map((label, i) => <li key={label} aria-current={i === step ? 'step' : undefined}>{label}</li>)}
    </ol>
    {!userId || problem === 'sign-in' ? <>
      <p><strong>{s.unmeasured}</strong> {s.signedOut}</p>
      <LinkButton href="/desk">{s.desk}</LinkButton>
    </> : <>
      {loading && !status && <p role="status">{s.loading}</p>}
      {problem && <p role="alert">{problem === 'reset' ? s.resetFailed : problem === 'refused' ? s.refused : s.unavailable}</p>}
      {measurement && <p className="optimization-journey__measurement" role="status">
        <strong>{measurement.state === 'ready' ? s.ready : measurement.state === 'recording' ? s.recording : measurement.state === 'incompatible' ? s.incompatible : missing ? s.unmeasured : s.waiting}</strong>
        {measurement.calls > 0 && <> {s.calls(measurement.calls)}</>}
      </p>}
      {missing && <p>{s.unmeasuredBody}</p>}
      {(phase === 'review' || phase === 'complete') && (measurement?.state === 'unmeasured' || measurement?.state === 'waiting') && <p className="note">{s.late}</p>}
      {(!session || missing || measurement?.state === 'incompatible') && !manual && <MeasuredAgentSetup userId={userId} target={target} />}

      {(!session || (phase === 'review' && !measurable) || (phase === 'complete' && !complete)) && <>
        {!confirmStart ? <Button variant="solid" disabled={busy || loading || blocked} onClick={() => setConfirmStart(true)}>{session ? s.startAgain : s.prepare}</Button> : <div className="optimization-journey__confirm" role="group" aria-label={s.confirm}>
          <p>{s.confirmBody}</p>
          <Button variant="solid" disabled={busy || blocked} onClick={() => void act({ action: 'start' })}>{s.confirm}</Button>
          <Button variant="quiet" disabled={busy} onClick={() => setConfirmStart(false)}>{s.cancel}</Button>
        </div>}
      </>}
      {phase === 'baseline' && <>
        <h3>{s.baseline}</h3><p>{s.ownerBrief}</p>
        {/* A one-task client may need a revised brief. Keep its command available after usage arrives. */}
        {!manual && <MeasuredAgentSetup userId={userId} target={target} />}
        <Button variant="quiet" disabled={busy || blocked} onClick={() => void reset()}>{s.restore}</Button>
      </>}
      {session && phase === 'review' && measurable && <>
        <h3>{s.baselineCost}</h3><p className="optimization-journey__cost">{usd(comparison?.before.costMicro ?? feedback!.attempt.costMicro)}</p>
        <p className="note">{s.first}. {s.priced} {feedback?.attempt.reported ? s.sourceReported : s.sourceGateway} {s.late}</p>
        {feedback?.usage && <>
          <h3>{s.passingUsage}</h3>
          <dl className="two-runs__figures">
            <div><dt>{s.inputTokens}</dt><dd>{number(feedback.usage.inputTokens)}</dd></div>
            <div><dt>{s.outputTokens}</dt><dd>{number(feedback.usage.outputTokens)}</dd></div>
            <div><dt>{s.cachedTokens}</dt><dd>{number(feedback.usage.cachedTokens)}</dd></div>
          </dl>
          <h4>{s.models}</h4>
          <ul className="two-runs__models">{feedback.usage.models.map((model) => <li key={model.model}>
            <span className="machine" dir="ltr">{model.model}</span><span>{s.calls(model.calls)}</span><span>{usd(model.costMicro)}</span>
          </li>)}</ul>
          {feedback.usage.models[0] && feedback.attempt.costMicro > 0 && <p>{s.topModel(`\u2066${feedback.usage.models[0].model}\u2069`, new Intl.NumberFormat(intlTag(locale), { style: 'percent', maximumFractionDigits: 0 }).format(Math.min(1, feedback.usage.models[0].costMicro / feedback.attempt.costMicro)))}</p>}
        </>}
        {rawIncident ? <p>{incidentCopy.observed(number(rawIncident.serviceCount), number(rawIncident.rawBytes))}</p>
          : incident ? <p>{coaching?.kind === 'no-opportunity' ? incidentCopy.noOpportunity : incidentCopy.unavailable}</p>
          : repeatedGit ? <h3>{snapshotCopy.observed(gitCommand, number(repeatedGit.count))}</h3>
          : snapshot ? <p>{coaching?.kind === 'no-opportunity' ? snapshotCopy.noOpportunity : snapshotCopy.unavailable}</p>
            : <><h3>{practice.practice}: {lesson.title}</h3><p>{practice.hypothesis}</p><p>{lesson.instruction}</p></>}
        {choices.length > 0 && <><fieldset className="optimization-journey__choices" disabled={busy || blocked}>
          <legend>{s.ownerChoice}</legend>
          {choices.map((technique) => <label key={technique}>
            <input type="radio" name={`${identity}-choice`} value={technique} disabled={technique === 'right-size-model' && !smallerModel} checked={choice === technique} onChange={() => setChoice(technique)} />
            <span><strong>{labels[technique]}</strong><small>{instructions[technique]}</small></span>
          </label>)}
        </fieldset>
        <p>{s.fresh}</p>
        {canChoose && choice !== 'right-size-model' && <p><strong>{practice.sameModel}</strong></p>}
        <Button variant="solid" disabled={busy || blocked || !canChoose} onClick={() => { if (canChoose) void act({ action: 'choose', sessionId: session.id, technique: choice }); }}>{s.choose}</Button>
        </>}
      </>}
      {session && phase === 'replay' && <>
        <h3>{s.replay}</h3>
        {session.technique && <p><strong>{s.chosen}: {labels[session.technique]}</strong> {instructions[session.technique]}</p>}
        {session.technique !== 'right-size-model' && <p><strong>{practice.sameModel}</strong></p>}
        <p>{s.fresh}</p><p>{s.ownerBrief}</p>
        {!manual && (replayModel ? <MeasuredAgentSetup userId={userId} target={target} measuredModel={replayModel} /> : <p role="status">{practice.modelUnknown}</p>)}
        <Button variant="quiet" disabled={busy || blocked} onClick={() => void reset()}>{s.restore}</Button>
      </>}
      {complete && comparison?.after && <>
        <h3>{s.complete}</h3>
        {session?.technique && <p>{s.chosen}: <strong>{labels[session.technique]}</strong></p>}
        <dl className="two-runs__pair"><div><dt>{s.first}</dt><dd>{usd(comparison.before.costMicro)}</dd></div><div><dt>{s.after}</dt><dd>{usd(comparison.after.costMicro)}</dd></div></dl>
        <p className="optimization-journey__cost">{delta === 0 ? s.equal : <>
          {comparison.before.costMicro > comparison.after.costMicro ? s.saved : s.dearer} {usd(delta)}{relativeCost && <> ({relativeCost})</>}
        </>}</p>
        <p className="note">{s.priced} {comparison.reported ? s.sourceReported : s.sourceGateway} {s.late} {s.caveat}</p>
        {repeatedGit && session?.technique === 'scope-context' && status?.sameReplayModel && comparison.after.costMicro < comparison.before.costMicro && <>
          <p>{snapshotCopy.cheaper}</p>
          <LinkButton href="/?view=course#pricing">{snapshotCopy.course}</LinkButton>
        </>}
        {rawIncident && session?.technique === 'scope-context' && status?.sameReplayModel && comparison.after.costMicro < comparison.before.costMicro && <>
          <p>{incidentCopy.cheaper}</p>
          <LinkButton href="/?view=course#pricing">{incidentCopy.course}</LinkButton>
        </>}
        {incident && onTransfer && <Button variant="solid" disabled={busy || blocked} onClick={onTransfer}>{incidentCopy.transfer}</Button>}
      </>}
      <Button variant="quiet" disabled={busy || loading} onClick={() => setRefresh((n) => n + 1)}>{s.retry}</Button>
    </>}
    {passed && <p className="optimization-journey__handoff"><strong>{s.returnOwner}</strong></p>}
    {!complete && <div className="optimization-journey__skip"><Button variant="quiet" onClick={continueWithout}>{keepIncident ? incidentCopy.keep : keepBaseline ? snapshotCopy.continue : s.continue}</Button>{!keepBaseline && !keepIncident && <p className="note">{s.continueNote}</p>}</div>}
  </section>;
}
